
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Tag } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ArticleHeaderProps {
  title: string;
  topics?: string[];
  author?: {
    id?: string | number;
    name?: string;
    bio?: string;
    avatar?: string;
    isFollowed?: boolean;
  };
  publishedAt: string;
  readTime?: number;
  isFollowing: boolean;
  setIsFollowing: (value: boolean) => void;
}

const ArticleHeader: React.FC<ArticleHeaderProps> = ({
  title,
  topics = [],
  author = {},
  publishedAt,
  readTime = 0,
  isFollowing,
  setIsFollowing
}) => {
  const navigate = useNavigate();
  
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
  };

  const displayReadTime = () => {
    const time = readTime && readTime > 0 ? readTime : 1;
    return `${time} min read`;
  };
  
  const handleTagClick = (topic: string) => {
    navigate(`/tags/${encodeURIComponent(topic)}`);
  };

  return (
    <header className="max-w-3xl mx-auto pt-8 px-4 md:px-0">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <TooltipProvider>
          {Array.isArray(topics) && topics.length > 0 ? (
            topics.map((topic, index) => (
              <Tooltip key={`${topic}-${index}`}>
                <TooltipTrigger asChild>
                  <Badge 
                    variant="outline" 
                    className="flex items-center gap-1 text-xs py-1 cursor-pointer hover:bg-muted"
                    onClick={() => handleTagClick(topic)}
                  >
                    <Tag className="h-3 w-3" />
                    {topic}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View more articles about {topic}</p>
                </TooltipContent>
              </Tooltip>
            ))
          ) : (
            <Badge variant="outline" className="flex items-center gap-1 text-xs py-1">
              <Tag className="h-3 w-3" />
              General
            </Badge>
          )}
        </TooltipProvider>
      </div>
      
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
        {title}
      </h1>
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Avatar className="h-12 w-12">
            <AvatarImage src={author.avatar} alt={author.name} />
            <AvatarFallback>{author.name ? author.name.substring(0, 2) : 'AU'}</AvatarFallback>
          </Avatar>
          
          <div>
            <Link to={`/journalist/${author.id}`} className="font-medium hover:underline">
              {author.name || 'Anonymous'}
            </Link>
            <p className="text-sm text-muted-foreground">{author.bio || 'No bio available'}</p>
          </div>
        </div>
        
        <Button 
          variant={isFollowing ? "default" : "outline"} 
          onClick={handleFollow}
          className="w-full md:w-auto"
        >
          {isFollowing ? "Following" : "Follow"}
        </Button>
      </div>
      
      <div className="flex items-center text-sm text-muted-foreground gap-4 mb-4">
        <div className="flex items-center gap-1">
          <Clock className="h-4 w-4" />
          <span>{displayReadTime()}</span>
        </div>
        <div>
          <span>{formatDate(publishedAt)}</span>
        </div>
      </div>
    </header>
  );
};

export default ArticleHeader;
